import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { lookupUser } from '../../api/dmAuth';
import { createConversation } from '../../api/dm';
import { ApiError } from '../../api/dmApiClient';

export default function DmNewConversation() {
  const navigate = useNavigate();
  const { accessToken, userId } = useDmPersona();
  const [mode, setMode] = useState('userId');
  const [userIdInput, setUserIdInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    let targetUserId = null;

    if (mode === 'userId') {
      const trimmed = userIdInput.trim();
      if (!trimmed) {
        setError(t('dm.newConversation.errors.userIdRequired'));
        return;
      }
      const n = Number(trimmed);
      if (!Number.isInteger(n) || n <= 0) {
        setError(t('dm.newConversation.errors.userIdInvalid'));
        return;
      }
      targetUserId = n;
    } else {
      const trimmed = usernameInput.trim();
      if (!trimmed) {
        setError(t('dm.newConversation.errors.usernameRequired'));
        return;
      }
      setLoading(true);
      try {
        const result = await lookupUser({ username: trimmed });
        targetUserId = result.userId;
        setInfo(
          t('dm.newConversation.lookupSuccess', {
            username: result.username || trimmed,
            userId: result.userId,
          })
        );
      } catch (err) {
        setLoading(false);
        if (err instanceof ApiError && err.status === 404) {
          setError(t('dm.newConversation.errors.lookupFailed'));
        } else {
          setError(err?.message || t('dm.newConversation.errors.lookupFailed'));
        }
        return;
      }
    }

    if (targetUserId === userId) {
      setLoading(false);
      setError(t('dm.newConversation.errors.selfTarget'));
      return;
    }

    setLoading(true);
    try {
      const conv = await createConversation(targetUserId, accessToken);
      if (conv?.conversationId) {
        navigate(`/dm/c/${conv.conversationId}`, { replace: true });
      } else {
        setError(t('dm.newConversation.errors.createFailed'));
      }
    } catch (err) {
      setError(err?.message || t('dm.newConversation.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <Link
        to="/dm/conversations"
        className="inline-flex items-center gap-2 text-sm text-dark-text-secondary hover:text-primary mb-6"
      >
        <ArrowLeft size={16} />
        {t('common.back')}
      </Link>
      <h1 className="text-xl sm:text-2xl font-bold mb-6">{t('dm.newConversation.title')}</h1>

      <div className="rounded-2xl bg-dark-card border border-dark-border p-5">
        <div className="flex items-center mb-5 rounded-full border border-dark-border p-1 bg-dark-surface">
          <button
            type="button"
            onClick={() => { setMode('userId'); setError(null); setInfo(null); }}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
              mode === 'userId' ? 'bg-primary text-black' : 'text-dark-text-secondary hover:text-primary'
            }`}
          >
            {t('dm.newConversation.modeUserId')}
          </button>
          <button
            type="button"
            onClick={() => { setMode('username'); setError(null); setInfo(null); }}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
              mode === 'username' ? 'bg-primary text-black' : 'text-dark-text-secondary hover:text-primary'
            }`}
          >
            {t('dm.newConversation.modeUsername')}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === 'userId' ? (
            <label className="block text-sm font-medium text-dark-text-secondary">
              {t('dm.newConversation.userIdLabel')}
              <input
                type="text"
                inputMode="numeric"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value.replace(/\D/g, ''))}
                placeholder={t('dm.newConversation.userIdPlaceholder')}
                className="mt-1 w-full bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
                autoFocus
              />
            </label>
          ) : (
            <label className="block text-sm font-medium text-dark-text-secondary">
              {t('dm.newConversation.usernameLabel')}
              <input
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                placeholder={t('dm.newConversation.usernamePlaceholder')}
                className="mt-1 w-full bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
                autoFocus
              />
            </label>
          )}

          {info && <p className="text-xs text-primary">{info}</p>}
          {error && (
            <p className="text-xs text-red-300 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-black font-bold py-2.5 rounded-lg disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            {loading ? t('common.loading') : t('dm.newConversation.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
